import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ROI_TASKS } from "../assumptions";
import { formatMoney, formatNumber } from "../selectors";

/*
 * Colours come from CSS variables (Pilots.css) so dark mode and the always-light
 * client presentation each get their own validated steps. Blue is always the
 * client's savings, orange is always what they pay Addy.
 */
const SAVINGS = "var(--pl-chart-savings)";
const ADDY_COST = "var(--pl-chart-cost)";
const STILL_HANDS_ON = "var(--pl-chart-kept)";
const GIVEN_BACK = "var(--pl-chart-freed)";
const AXIS_TICK = { fontSize: 11, fill: "var(--pl-muted)" };

const TOOLTIP_STYLE = {
  background: "var(--pl-surface)",
  border: "1px solid var(--pl-border-strong)",
  borderRadius: 10,
  fontSize: 12,
  color: "var(--pl-text)",
};

// Legend text stays in the text colour; the swatch beside it carries the series colour.
const legendText = (value) => <span style={{ color: "var(--pl-muted)" }}>{value}</span>;

const compactMoney = (value) => {
  if (Math.abs(value) >= 1000000) {
    return `$${formatNumber(value / 1000000, 1)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `$${formatNumber(value / 1000, 1)}K`;
  }

  return formatMoney(value);
};

/** Fixed size when printing a slide, responsive on the page. */
function ChartFrame({ width, height, children }) {
  if (width) {
    return React.cloneElement(children, { width, height });
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      {children}
    </ResponsiveContainer>
  );
}

/** What the client saves in staff time against what they pay Addy, per year. */
export function ValueVsCostChart({ result, width, height = 150 }) {
  const data = [
    { label: "Staff time saved", value: Math.max(result.annualLaborSavings, 0), fill: SAVINGS },
    { label: "Addy", value: result.annualAddyCost, fill: ADDY_COST },
  ];

  return (
    <ChartFrame width={width} height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 72, bottom: 4, left: 8 }}>
        <CartesianGrid horizontal={false} stroke="var(--pl-track)" />
        <XAxis type="number" tick={AXIS_TICK} tickFormatter={compactMoney} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey="label" width={118} tick={AXIS_TICK} axisLine={false} tickLine={false} />
        <Tooltip
          cursor={{ fill: "var(--pl-track)", opacity: 0.4 }}
          contentStyle={TOOLTIP_STYLE}
          formatter={(value) => [formatMoney(value), "Per year"]}
        />
        <Bar dataKey="value" barSize={22} radius={[0, 4, 4, 0]} isAnimationActive={false}>
          {data.map((entry) => (
            <Cell key={entry.label} fill={entry.fill} />
          ))}
          <LabelList
            dataKey="value"
            position="right"
            formatter={compactMoney}
            style={{ fontSize: 12, fontWeight: 700, fill: "var(--pl-text)" }}
          />
        </Bar>
      </BarChart>
    </ChartFrame>
  );
}

/** Each task's minutes per loan today, split into what stays hands-on and what Addy gives back. */
export function TaskMinutesChart({ result, width, height }) {
  const data = result.tasks
    .filter((task) => task.minutesToday > 0)
    .map((task) => ({
      label: ROI_TASKS.find((entry) => entry.key === task.key)?.label || task.key,
      kept: task.minutesWithAddy,
      freed: task.minutesSaved,
      today: task.minutesToday,
    }));
  const chartHeight = height || 60 + data.length * 44;

  if (!data.length) {
    return <div className="pl-empty">Add minutes to at least one task to see the chart.</div>;
  }

  return (
    <ChartFrame width={width} height={chartHeight}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 56, bottom: 4, left: 8 }}>
        <CartesianGrid horizontal={false} stroke="var(--pl-track)" />
        <XAxis
          type="number"
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          tickFormatter={(value) => `${value} min`}
        />
        <YAxis type="category" dataKey="label" width={210} tick={AXIS_TICK} axisLine={false} tickLine={false} />
        <Tooltip
          cursor={{ fill: "var(--pl-track)", opacity: 0.4 }}
          contentStyle={TOOLTIP_STYLE}
          formatter={(value, name) => [`${formatNumber(value, 1)} min`, name]}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} formatter={legendText} />
        <Bar
          dataKey="kept"
          name="Still hands-on with Addy"
          stackId="minutes"
          fill={STILL_HANDS_ON}
          stroke="var(--pl-surface)"
          strokeWidth={2}
          barSize={22}
          isAnimationActive={false}
        />
        <Bar
          dataKey="freed"
          name="Given back by Addy"
          stackId="minutes"
          fill={GIVEN_BACK}
          stroke="var(--pl-surface)"
          strokeWidth={2}
          barSize={22}
          radius={[0, 4, 4, 0]}
          isAnimationActive={false}
        >
          <LabelList
            dataKey="today"
            position="right"
            formatter={(value) => `${formatNumber(value, 1)} min`}
            style={{ fontSize: 11, fill: "var(--pl-muted)" }}
          />
        </Bar>
      </BarChart>
    </ChartFrame>
  );
}

/** Running totals: staff time saved against Addy's cost, year by year. */
export function ForecastChart({ forecast, width, height = 240 }) {
  return (
    <ChartFrame width={width} height={height}>
      <BarChart data={forecast} margin={{ top: 22, right: 12, bottom: 0, left: 4 }} barGap={2}>
        <CartesianGrid vertical={false} stroke="var(--pl-track)" />
        <XAxis dataKey="label" tick={AXIS_TICK} axisLine={{ stroke: "var(--pl-border)" }} tickLine={false} />
        <YAxis tick={AXIS_TICK} tickFormatter={compactMoney} axisLine={false} tickLine={false} width={56} />
        <Tooltip
          cursor={{ fill: "var(--pl-track)", opacity: 0.4 }}
          contentStyle={TOOLTIP_STYLE}
          formatter={(value, name) => [formatMoney(value), name]}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} formatter={legendText} />
        <Bar
          dataKey="savings"
          name="Staff time saved (running total)"
          fill={SAVINGS}
          barSize={24}
          radius={[4, 4, 0, 0]}
          isAnimationActive={false}
        >
          <LabelList
            dataKey="savings"
            position="top"
            formatter={compactMoney}
            style={{ fontSize: 11, fontWeight: 700, fill: "var(--pl-text)" }}
          />
        </Bar>
        <Bar
          dataKey="addyCost"
          name="Addy (running total)"
          fill={ADDY_COST}
          barSize={24}
          radius={[4, 4, 0, 0]}
          isAnimationActive={false}
        />
      </BarChart>
    </ChartFrame>
  );
}

/** The numbers behind the money charts, for anyone who cannot use the charts. */
export function ForecastTable({ forecast }) {
  return (
    <table className="pl-table pl-forecast-table">
      <thead>
        <tr>
          <th className="pl-left">Running total</th>
          <th>Staff time saved</th>
          <th>Addy</th>
          <th>Net</th>
        </tr>
      </thead>
      <tbody>
        {forecast.map((row) => (
          <tr key={row.year}>
            <td className="pl-left">{row.label}</td>
            <td>{formatMoney(row.savings)}</td>
            <td>{formatMoney(row.addyCost)}</td>
            <td className="pl-strong">{formatMoney(row.net)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
